/**
 * The static bodies of the Proposal tab's three Application detail panes —
 * `proposal.tmpl:225-367`, transcribed literally.
 *
 * ★ THESE ARE MOCK-UPS, NOT DATA. Every number, tag and material string below is
 * hard-coded in the Handlebars template: "AHU-1 / 1 / 171,500 / 171,500", "Iran
 * Cast-C3x6", "Dwyer 2001-SF". They are a preview of what the generated Proposal
 * document will look like, not the user's project. Nothing here is fetched and
 * nothing here is editable — which is why this file has no props beyond the slug.
 *
 * ★ THE SUBMITTAL EQUIVALENT IS COMPLETELY DIFFERENT. Submittal's 20 application
 * panes are one `<img>` each, driven by the `APP_PREVIEW_IMAGES` table
 * (`submittal/appPreviewImages.ts`). Proposal has THREE panes and only ONE of
 * them is an image; the other two are inline markup. There is nothing to share.
 *
 * ★ A slug with no branch here renders an empty `<div class="col-xs-12 <value>">`
 * — exactly what the legacy `{{#ifCompare}}` chain does for an unmatched value.
 */

const IMAGE_BASE = '/static/user_settings/images/';

/** `proposal.tmpl:226-251` — the Price Summary mock table. */
const PriceSummary = () => (
  <div className="col-xs-12 non-editable">
    <div className="row app-feilds-table-header">
      <div className="col-xs-4">Tag</div>
      <div className="col-xs-1 float-right">Qty</div>
      <div className="col-xs-3 float-right">Unit Price (US$)</div>
      <div className="col-xs-4 float-right">Extended Price (US$)</div>
    </div>
    {[
      ['AHU-1', '171,500', '171,500'],
      ['AHU-2', '144,000', '144,000'],
      ['AHU-3', '228,000', '228,000'],
    ].map(([tag, unit, extended]) => (
      <div className="row app-feilds-table-content" key={tag}>
        <div className="col-xs-4">{tag}</div>
        <div className="col-xs-1 float-right">1</div>
        <div className="col-xs-3 float-right">{unit}</div>
        <div className="col-xs-4 float-right">{extended}</div>
      </div>
    ))}
  </div>
);

/**
 * `proposal.tmpl:259-366` — the Equipment Description mock.
 *
 * The template is a flat run of `.row`s in four shapes; they are transcribed as
 * data because the SHAPES repeat verbatim and the DOM is identical either way.
 *  · `header`  → `<div class="col-xs-12">label</div>`
 *  · `group`   → `<div class="col-xs-3">label</div><div class="col-xs-9">value</div>`
 *  · `detail`  → `<div class="col-xs-2 col-xs-offset-1">label</div>`
 *                `<div class="col-xs-8">value</div><div class="col-xs-1">tag</div>`
 *  · `wide`    → `<div class="col-xs-3">label</div><div class="col-xs-8">value</div>`
 *                `<div class="col-xs-1">tag</div>`   (the two Coil rows)
 */
type DescRow =
  | { kind: 'header'; label: string }
  | { kind: 'group'; label: string; value: string }
  | { kind: 'detail'; label: string; value: string; tag?: string }
  | { kind: 'wide'; label: string; value: string; tag?: string };

const EQUIPMENT_DESCRIPTION: DescRow[] = [
  { kind: 'header', label: 'Agency Listing ETL, OSHPD' },
  { kind: 'header', label: 'Cabinet' },
  { kind: 'detail', label: 'Split / D-Mount', value: '2' },
  { kind: 'detail', label: 'Finish', value: 'Epoxy 2-Coat' },
  { kind: 'detail', label: 'Casing', value: 'Steel-Galv ZC-G90-sheet-Solid-16ga' },
  { kind: 'detail', label: 'Insulation', value: 'Fiberglass, 2in 3pcf' },
  { kind: 'detail', label: 'Liner', value: 'Steel-Galv ZC-G90-sheet-Perforated-22ga' },
  { kind: 'group', label: 'Base', value: 'Iran Cast-C3x6' },
  { kind: 'detail', label: 'Floor Deck', value: 'Steel-Galv ZC-G90-sheet-Solid-14ga' },
  { kind: 'detail', label: 'Insulation', value: 'Fiberglass, 2in 3pcf' },
  { kind: 'detail', label: 'Under-Liner', value: 'Steel-Galv ZC-G90-sheet-Solid-22ga' },
  { kind: 'group', label: 'Fan', value: 'Twin City, EPFN, Arr 4' },
  { kind: 'detail', label: 'Motors', value: 'ABB, XE Premium, TEFC, Cast Iron' },
  { kind: 'detail', label: 'Isolators', value: 'The VMC Group, AMSR-1E, Spring' },
  {
    kind: 'wide',
    label: 'Coil',
    value:
      'Modine, Fluid, Heating, 5/8 Copper .020 tubes, Aluminum .006 mill finish fins, Steel-S-304 casing',
  },
  {
    kind: 'wide',
    label: 'Coil',
    value:
      'Modine, Fluid, Cooling, 5/8 Copper .020 tubes, Copper .006 mill finish fins, Steel-S-304 casing',
    tag: 'AHU-3',
  },
  {
    kind: 'wide',
    label: 'Filter',
    value: 'Camfil 30/30, MERV8, 4in, Supplied by Manufacturer',
  },
  { kind: 'detail', label: 'Frames', value: 'Camfil, Type-8, Front Face, Steel-Galv' },
  { kind: 'detail', label: 'Gage', value: 'Dwyer 2001-SF' },
  {
    kind: 'wide',
    label: 'Filter',
    value: 'Camfil Durafil-ES, MERV11, 12in, Supplied by Manufacturer',
    tag: 'AHU-3',
  },
  { kind: 'detail', label: 'Frames', value: 'Camfil, Type-8, Front Face, Steel-Galv' },
  { kind: 'detail', label: 'Gage', value: 'Dwyer 2002-SF' },
];

const EquipmentDescription = () => (
  <div className="col-xs-12 non-editable equipment-description">
    {EQUIPMENT_DESCRIPTION.map((row, i) => {
      // eslint-disable-next-line react/no-array-index-key
      const key = `${row.kind}-${row.label}-${i}`;
      if (row.kind === 'header') {
        return (
          <div className="row" key={key}>
            <div className="col-xs-12">{row.label}</div>
          </div>
        );
      }
      if (row.kind === 'group') {
        return (
          <div className="row" key={key}>
            <div className="col-xs-3">{row.label}</div>
            <div className="col-xs-9">{row.value}</div>
          </div>
        );
      }
      if (row.kind === 'wide') {
        return (
          <div className="row" key={key}>
            <div className="col-xs-3">{row.label}</div>
            <div className="col-xs-8">{row.value}</div>
            <div className="col-xs-1">{row.tag ?? ''}</div>
          </div>
        );
      }
      return (
        <div className="row" key={key}>
          <div className="col-xs-2 col-xs-offset-1">{row.label}</div>
          <div className="col-xs-8">{row.value}</div>
          <div className="col-xs-1">{row.tag ?? ''}</div>
        </div>
      );
    })}
  </div>
);

const ProposalAppPaneBody = ({ value }: { value: string }) => {
  if (value === 'price_summary') return <PriceSummary />;
  if (value === 'equipment_list') {
    return (
      <div className="content-image">
        <img src={`${IMAGE_BASE}equipment_list.jpg`} alt="" />
      </div>
    );
  }
  if (value === 'equipment_description') return <EquipmentDescription />;
  return null;
};

export default ProposalAppPaneBody;
